import { Link } from "react-router-dom";
import { SaveButton } from "@/components/site/SaveButton";
import { ExternalLink, Scale, Tag, TrendingDown } from "lucide-react";
import { goUrl, inr } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useCompare } from "@/components/site/compare";

export const DemoBadge = ({ show }) =>
  show ? (
    <span data-testid="demo-badge" className="bg-purple-100 text-purple-700 text-[10px] font-bold px-2 py-0.5 rounded border border-purple-200 uppercase tracking-wide">
      Demo data
    </span>
  ) : null;

export const PriceDropBadge = ({ deal, className = "" }) => {
  const siteName = useSite().name;
  if (deal.status === "expired") return null;
  if (deal.lowest_recorded)
    return (
      <span data-testid="lowest-recorded-badge" title={`Based only on prices ${siteName} has recorded, not the whole market`}
        className={`inline-flex items-center gap-1 bg-emerald-700 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow ${className}`}>
        <TrendingDown size={12} /> Lowest we've recorded
      </span>
    );
  if (deal.price_drop_pct > 0)
    return (
      <span data-testid="price-drop-badge" className={`inline-flex items-center gap-1 bg-amber-500 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow ${className}`}>
        <TrendingDown size={12} /> Dropped {deal.price_drop_pct}%
      </span>
    );
  return null;
};

export const PriceRow = ({ price, mrp, discount, size = "base" }) => (
  <div className="flex items-baseline flex-wrap gap-x-2" data-testid="price-row">
    <span className={`font-display font-extrabold text-slate-900 ${size === "lg" ? "text-4xl" : "text-xl"}`} data-testid="deal-price">{inr(price)}</span>
    {mrp ? <span className="text-sm text-slate-400 line-through" data-testid="deal-mrp">{inr(mrp)}</span> : null}
    {discount > 0 && <span className="text-sm font-bold text-emerald-700" data-testid="deal-discount-text">{discount}% off</span>}
  </div>
);

export const DealCard = ({ deal, i = 0 }) => {
  const { has, toggle } = useCompare();
  const inCompare = has(deal.id);
  return (
    <article
      data-testid={`deal-card-${deal.slug}`}
      className="relative rise bg-white rounded-xl border border-slate-200 hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow,border-color] duration-200 overflow-hidden flex flex-col group"
      style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
    >
      <Link to={`/deal/${deal.slug}`} className="relative h-44 bg-slate-50 border-b border-slate-100 overflow-hidden block" data-testid="deal-card-image-link">
        <img src={deal.image_url} alt={deal.alt_text || deal.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {deal.discount_pct > 0 && (
          <span className="absolute top-3 left-3 bg-red-600 text-white font-black text-xs px-2.5 py-1 rounded-md shadow-sm" data-testid="deal-card-discount-badge">
            {deal.discount_pct}% OFF
          </span>
        )}
        <span className="absolute top-3 right-3"><DemoBadge show={deal.is_demo} /></span>
        <PriceDropBadge deal={deal} className="absolute bottom-3 left-3" />
      </Link>
      <SaveButton deal={deal} className="absolute top-[8.25rem] right-3" />
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span className="font-semibold text-slate-700">{deal.merchant_name}</span>
          <span>{deal.category_name}</span>
        </div>
        <Link to={`/deal/${deal.slug}`} className="font-semibold text-slate-900 leading-snug line-clamp-2 hover:text-emerald-700" data-testid="deal-card-title">
          {deal.title}
        </Link>
        <PriceRow price={deal.price} mrp={deal.mrp} discount={deal.discount_pct} />
        {deal.coupon_code && (
          <span className="inline-flex items-center gap-1 text-xs font-mono font-bold text-amber-900 bg-amber-50 border border-dashed border-amber-400 rounded px-2 py-0.5 w-fit">
            <Tag size={12} /> {deal.coupon_code}
          </span>
        )}
        <div className="mt-auto pt-2 flex gap-2">
          <a
            href={goUrl(deal.id, "card")}
            target="_blank"
            rel="sponsored nofollow noopener"
            data-testid="deal-card-get-deal-button"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
          >
            Get Deal <ExternalLink size={14} />
          </a>
          <button
            onClick={() => toggle(deal.id)}
            data-testid="deal-card-compare-button"
            aria-label="Add to compare"
            className={`px-3 rounded-lg border transition-colors ${inCompare ? "bg-amber-100 border-amber-400 text-amber-800" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
          >
            <Scale size={16} />
          </button>
        </div>
      </div>
    </article>
  );
};

export const DealGrid = ({ deals, testid = "deal-grid" }) => (
  <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
    {deals.map((d, i) => <DealCard key={d.id} deal={d} i={i} />)}
  </div>
);

export const DealGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-80 rounded-xl bg-slate-200/60 animate-pulse" />)}
  </div>
);
